import type { League, LeagueDivision } from './season-results';

export const BETA_SEASON_TWO_ID = 'S2';
export const BETA_SEASON_TWO_NAME = 'Season 2';
export const BETA_SEASON_TWO_LEAGUE_ID = 'DFL';
export const BETA_SEASON_TWO_LEAGUE_NAME = 'MF DFL';
export const BETA_SEASON_TWO_START_DATE = '2026-08-16';
export const BETA_SEASON_TWO_PLAYOFF_START_DATE = '2026-08-27';
export const BETA_SEASON_TWO_CHAMPIONSHIP_DATE = '2026-08-30';
export const BETA_SEASON_TWO_FIRST_KICKOFF_UTC_HOUR = 20; // 3:00 PM Central (CDT)
export const BETA_SEASON_TWO_GAMES_PER_KICKOFF_SLOT = 3;
export const BETA_SEASON_TWO_KICKOFF_INTERVAL_MINUTES = 30; // 3:00 through 8:30 PM Central
export const BETA_SEASON_TWO_PLAYOFF_FIRST_KICKOFF_UTC_HOUR = 24; // 7:00 PM Central (CDT)
export const BETA_SEASON_TWO_PLAYOFF_GAMES_PER_KICKOFF_SLOT = 1;
export const BETA_SEASON_TWO_PLAYOFF_KICKOFF_INTERVAL_MINUTES = 30;
export const BETA_SEASON_TWO_ROUNDS_PER_DAY = 3;
export const BETA_SEASON_TWO_PLAYOFF_TEAMS = 12 as const;

/**
 * Kickoff slot for each game in each 36-game regular-season slate. This fixed
 * 24-team assignment gives every team exactly two games in every one of the 12
 * season-long slots, while keeping its three games on a given day in distinct
 * slots. Each slot holds exactly three simultaneous games.
 */
export const BETA_SEASON_TWO_KICKOFF_SLOT_ASSIGNMENTS: readonly (readonly number[])[] = [
  [0, 4, 3, 3, 8, 10, 10, 4, 9, 11, 9, 2, 6, 2, 0, 9, 8, 0, 2, 7, 5, 7, 3, 6, 1, 10, 7, 11, 5, 6, 1, 4, 5, 1, 11, 8],
  [4, 4, 8, 11, 5, 4, 11, 2, 5, 9, 0, 6, 10, 5, 2, 3, 6, 2, 0, 7, 3, 7, 0, 10, 9, 1, 11, 3, 7, 8, 10, 1, 1, 9, 8, 6],
  [1, 7, 2, 3, 8, 5, 2, 5, 10, 4, 5, 1, 8, 4, 4, 1, 0, 7, 11, 3, 10, 7, 3, 6, 6, 0, 9, 11, 8, 6, 11, 2, 9, 0, 9, 10],
  [5, 5, 9, 0, 4, 0, 2, 0, 1, 11, 11, 5, 3, 6, 7, 7, 4, 3, 11, 8, 10, 7, 10, 6, 2, 10, 1, 8, 1, 8, 6, 9, 9, 4, 2, 3],
  [5, 0, 4, 1, 10, 2, 6, 2, 2, 11, 7, 9, 3, 6, 9, 7, 8, 0, 1, 5, 0, 4, 7, 8, 10, 5, 11, 4, 3, 3, 11, 8, 9, 1, 10, 6],
  [0, 2, 2, 5, 8, 6, 7, 3, 4, 3, 10, 5, 4, 11, 7, 10, 9, 11, 1, 10, 0, 5, 7, 6, 9, 3, 4, 1, 8, 8, 6, 11, 9, 1, 0, 2],
  [8, 7, 2, 3, 1, 4, 1, 1, 10, 6, 5, 11, 11, 0, 10, 9, 8, 5, 9, 4, 3, 0, 8, 10, 7, 4, 2, 5, 6, 0, 2, 11, 3, 7, 6, 9],
  [2, 8, 5, 4, 11, 8, 7, 7, 10, 10, 8, 2, 7, 1, 9, 6, 1, 3, 1, 6, 9, 2, 0, 4, 11, 6, 0, 4, 3, 5, 10, 11, 3, 9, 5, 0],
] as const;

export type BetaSeasonTwoTeam = {
  tokenId: string;
  division: 'W' | 'S' | 'N' | 'E';
  city: string;
  name: string;
};

/** Token order and names supplied for the 24-team beta season. */
export const BETA_SEASON_TWO_TEAMS: readonly BetaSeasonTwoTeam[] = [
  { tokenId: '51', division: 'W', city: 'Boise', name: 'Bison' },
  { tokenId: '52', division: 'W', city: 'Denver', name: 'Dimes' },
  { tokenId: '53', division: 'W', city: 'Sactown', name: 'Brawlers' },
  { tokenId: '54', division: 'W', city: 'San Diego', name: 'Sharks' },
  { tokenId: '55', division: 'W', city: 'Midwest', name: 'Magic' },
  { tokenId: '56', division: 'W', city: 'Los Angeles', name: 'Degens' },
  { tokenId: '57', division: 'S', city: 'Vegas', name: 'Golden Pines' },
  { tokenId: '58', division: 'S', city: 'Dallas', name: 'Drifters' },
  { tokenId: '59', division: 'S', city: 'Florida', name: 'Leos' },
  { tokenId: '60', division: 'S', city: 'Louisiana', name: 'Cats' },
  { tokenId: '61', division: 'S', city: 'Rocky Mount', name: 'Maulers' },
  { tokenId: '62', division: 'S', city: 'Fort Pierce', name: 'Farmers' },
  { tokenId: '63', division: 'N', city: 'Bay City', name: 'Sugar Beats' },
  { tokenId: '64', division: 'N', city: 'Appleton', name: 'Admirals' },
  { tokenId: '65', division: 'N', city: 'Ashland', name: 'Runescapes' },
  { tokenId: '66', division: 'N', city: 'Boston', name: 'Bad Moons' },
  { tokenId: '67', division: 'N', city: 'Toronto', name: 'Wolfpack' },
  { tokenId: '68', division: 'N', city: 'Ashland', name: 'Outlawz' },
  { tokenId: '69', division: 'E', city: 'Chicago', name: 'Mobsters' },
  { tokenId: '70', division: 'E', city: 'Pennsylvania', name: 'Coal Miners' },
  { tokenId: '71', division: 'E', city: 'Buffalo', name: 'Bullies' },
  { tokenId: '72', division: 'E', city: 'Hudson River', name: 'Whalers' },
  { tokenId: '73', division: 'E', city: 'Long Island', name: 'Mariners' },
  { tokenId: '74', division: 'E', city: 'Philadelphia', name: 'Stars' },
] as const;

/** Stable membership used before the Season 2 registry has been created. */
export const BETA_SEASON_TWO_TEAM_IDS = BETA_SEASON_TWO_TEAMS.map((team) => team.tokenId);

const DIVISION_NAMES: Record<BetaSeasonTwoTeam['division'], string> = {
  W: 'West',
  S: 'South',
  N: 'North',
  E: 'East',
};

export function buildBetaSeasonTwoLeague(): League {
  const divisions: LeagueDivision[] = (['W', 'S', 'N', 'E'] as const).map((key) => ({
    id: `${BETA_SEASON_TWO_LEAGUE_ID}-${key}`,
    name: DIVISION_NAMES[key],
    teamIds: BETA_SEASON_TWO_TEAMS
      .filter((team) => team.division === key)
      .map((team) => team.tokenId),
  }));

  return {
    id: BETA_SEASON_TWO_LEAGUE_ID,
    name: BETA_SEASON_TWO_LEAGUE_NAME,
    playoffTeamCount: BETA_SEASON_TWO_PLAYOFF_TEAMS,
    conferences: [{
      id: BETA_SEASON_TWO_LEAGUE_ID,
      name: BETA_SEASON_TWO_LEAGUE_NAME,
      divisions,
    }],
  };
}
